/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { CellStyle, ColDef, ICellRendererParams } from 'ag-grid-community';

import {
  numberAsStringComparator,
  threeDecimalValueFormatter,
  dateValueFormatter,
  incompleteCellRenderer,
  twoDecimalValueFormatter,
  TEXT_WIDTH,
  TWO_DIGIT_WIDTH,
  THREE_DIGIT_WIDTH,
  FOUR_DIGIT_WIDTH,
  FIVE_DIGIT_WIDTH,
  SESSION_TIME_WIDTH,
  stringToNumComparator,
  unverifiedCellStyle,
  booleanCellRenderer,
  SIX_DIGIT_WIDTH,
  flagCellRenderer,
} from 'components/common/Tables/CommonGridFormatters';
import { PitRoadSummaryDataObject } from './PitRoadObject';
import { PitStrategySource } from 'models/viewModels';

export enum PitRoadTableColId {
  VehicleNumber = 'vehicleNumber',
  Lap = 'lap',
  FlagIn = 'flagIn',
  FlagOut = 'flagOut',
  LeftTime = 'leftTime',
  RightTime = 'rightTime',
  JackTime = 'jackTime',
  CrewTime = 'crewTime',
  DriverTime = 'driverTime',
  DriverEnter = 'driverEnter',
  DriverGoes = 'driverGoes',
  VehicleStopToJack = 'vehicleStopToJack',
  JackToVehicleGoes = 'jackToVehicleGoes',
  TotalTime = 'totalTime',
  TiresChanged = 'tiresChanged',
  PitInTime = 'pitInTime',
  PitOutTime = 'pitOutTime',
  MissedEvents = 'missedEvents',
  Sequence = 'sequence',
  LeaderLap = 'leaderLap',
  DriverName = 'driverFullName',
  PitStopType = 'pitStopType',
  verifiedPitStopType = 'verifiedPitStopType',
  Track = 'eventName',
  Date = 'sessionDate',
  PitInLap = 'pitInLap',
  PitInPosition = 'pitInPosition',
  PitOutPosition = 'pitOutPosition',
  PitPositionDifference = 'pitPositionDifference',
  AvgPitInPosition = 'avgPitInPosition',
  AvgPitOutPosition = 'avgPitOutPosition',
  AvgPitPositionDifference = 'avgPitPositionDifference',
  GreenIn = 'greenIn',
  GreenOut = 'greenOut',
  ProPitStopType = 'proPitStopType',
  PitBoxStopTime = 'pitBoxStopTime',
  PitBoxLeaveTime = 'pitBoxLeaveTime',
  PitStopDuration = 'pitStopDuration',
  LeftSideAdjustment = 'leftSideAdjust',
  RightSideAdjustment = 'rightSideAdjust',
  NumberOfFuelCans = 'numberOfFuelCans',
  Infractions = 'infractions',
  FrontRightTireChanged = 'frontRightTireChanged',
  FrontLeftTireChanged = 'frontLeftTireChanged',
  RearRightTireChanged = 'rearRightTireChanged',
  RearLeftTireChanged = 'rearLeftTireChanged',
  BodyRepairs = 'bodyRepairs',
}

// These columns are currently not supported or incorrect from ERDP
const erdpUnsupportedCols = [
  PitRoadTableColId.MissedEvents.valueOf(),
  PitRoadTableColId.PitInTime.valueOf(),
  PitRoadTableColId.PitOutTime.valueOf(),
];

const ppStratCellStyle = (data: PitRoadSummaryDataObject) => {
  // italicize if the pit stop type is not from PITRHO
  return data.verifiedPitStopType && data.source && data.source.toString() !== PitStrategySource.PITRHO.toString()
    ? ({
        ...unverifiedCellStyle,
        textAlign: 'left',
      } as CellStyle)
    : undefined;
};

export const unsupportedCellRenderer = (props: ICellRendererParams) => {
  return props.value === undefined ||
    props.value === null ||
    // If it's 0, we need to check that it's because ERDP doesn't support that property at this time
    (props.value === 0 && erdpUnsupportedCols.includes(props.column?.getColId() || ''))
    ? '-'
    : props.value;
};

export const pitRoadTableColDefs: ColDef[] = [
  {
    colId: PitRoadTableColId.VehicleNumber,
    headerName: 'Vehicle',
    headerTooltip: 'Vehicle number',
    cellClass: 'stringType',
    comparator: numberAsStringComparator,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: [],
      closeOnApply: true,
      inRangeInclusive: true,
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: TWO_DIGIT_WIDTH,
  },
  {
    colId: PitRoadTableColId.DriverName,
    headerName: 'Driver',
    headerTooltip: "Driver's name",
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: [],
      closeOnApply: true,
      cellHeight: 25,
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: TEXT_WIDTH,
    flex: 1,
  },
  {
    colId: PitRoadTableColId.Lap,
    headerName: 'Lap',
    headerTooltip: 'Lap when vehicle enters pit road',
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: THREE_DIGIT_WIDTH,
  },
  {
    colId: PitRoadTableColId.LeaderLap,
    headerName: 'Leader Lap',
    headerTooltip: 'Leader lap when vehicle enters pit road',
    sort: 'desc',
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: THREE_DIGIT_WIDTH,
  },
  {
    colId: PitRoadTableColId.PitStopType,
    headerName: 'Tires',
    headerTooltip: 'Pit stop type',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: [],
      closeOnApply: true,
      cellHeight: 25,
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: 42,
  },
  {
    colId: PitRoadTableColId.verifiedPitStopType,
    headerName: 'Verified Tires',
    headerTooltip: 'Verified Pit stop type',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: [],
      closeOnApply: true,
      cellHeight: 25,
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: 42,
    cellStyle: (cell) => ppStratCellStyle(cell.data),
  },
  {
    colId: PitRoadTableColId.PitInTime,
    headerName: 'Pit In',
    headerTooltip: 'Time of session when vehicle enters pit road',
    valueFormatter: twoDecimalValueFormatter,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
      allowedCharPattern: '\\d\\-', // allow decimals and negatives.
      numberParser: (text: string | null) => {
        return text == null ? null : parseFloat(text);
      },
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: SESSION_TIME_WIDTH,
  },
  {
    colId: PitRoadTableColId.PitOutTime,
    headerName: 'Pit Out',
    headerTooltip: 'Time of session when vehicle exits pit road',
    valueFormatter: twoDecimalValueFormatter,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
      allowedCharPattern: '\\d\\-', // allow decimals and negatives.
      numberParser: (text: string | null) => {
        return text == null ? null : parseFloat(text);
      },
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: SESSION_TIME_WIDTH,
  },
  {
    colId: PitRoadTableColId.TotalTime,
    headerName: 'Tot Tm',
    headerTooltip: 'Total time spent on pit road, from “pit-in” loop to “pit-out” loop',
    valueFormatter: twoDecimalValueFormatter,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
      allowedCharPattern: '\\d\\-', // allow decimals and negatives.
      numberParser: (text: string | null) => {
        return text == null ? null : parseFloat(text);
      },
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: FIVE_DIGIT_WIDTH,
  },
  {
    colId: PitRoadTableColId.DriverTime,
    headerName: 'Drv Tm',
    headerTooltip: 'Time spent driving on pit road',
    valueFormatter: twoDecimalValueFormatter,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
      allowedCharPattern: '\\d\\-', // allow decimals and negatives.
      numberParser: (text: string | null) => {
        return text == null ? null : parseFloat(text);
      },
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: FIVE_DIGIT_WIDTH,
  },
  {
    colId: PitRoadTableColId.CrewTime,
    headerName: 'Crew Tm',
    headerTooltip:
      'Time spent working on the vehicle by pit crew; calculated from vehicle stop to left (or right) down',
    valueFormatter: twoDecimalValueFormatter,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
      allowedCharPattern: '\\d\\-', // allow decimals and negatives.
      numberParser: (text: string | null) => {
        return text == null ? null : parseFloat(text);
      },
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: FIVE_DIGIT_WIDTH,
  },
  {
    colId: PitRoadTableColId.DriverEnter,
    headerName: 'Drv Ent',
    headerTooltip: 'Time from pit road entrance to vehicle stop in pit stall',
    valueFormatter: twoDecimalValueFormatter,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
      allowedCharPattern: '\\d\\-', // allow decimals and negatives.
      numberParser: (text: string | null) => {
        return text == null ? null : parseFloat(text);
      },
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: FOUR_DIGIT_WIDTH,
  },
  {
    colId: PitRoadTableColId.VehicleStopToJack,
    headerName: 'Pre Jack',
    headerTooltip: 'Time from vehicle stop to right (or left) side of vehicle lifted',
    valueFormatter: twoDecimalValueFormatter,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
      allowedCharPattern: '\\d\\-', // allow decimals and negatives.
      numberParser: (text: string | null) => {
        return text == null ? null : parseFloat(text);
      },
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: FOUR_DIGIT_WIDTH,
  },
  {
    colId: PitRoadTableColId.RightTime,
    headerName: 'Right Tm',
    headerTooltip: 'Time spent with the right side of vehicle up',
    valueFormatter: twoDecimalValueFormatter,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
      allowedCharPattern: '\\d\\-', // allow decimals and negatives.
      numberParser: (text: string | null) => {
        return text == null ? null : parseFloat(text);
      },
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: FOUR_DIGIT_WIDTH,
  },
  {
    colId: PitRoadTableColId.JackTime,
    headerName: 'Around',
    headerTooltip: 'Time spent moving the jack between sides',
    valueFormatter: twoDecimalValueFormatter,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
      allowedCharPattern: '\\d\\-', // allow decimals and negatives.
      numberParser: (text: string | null) => {
        return text == null ? null : parseFloat(text);
      },
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: FOUR_DIGIT_WIDTH,
  },
  {
    colId: PitRoadTableColId.LeftTime,
    headerName: 'Left Tm',
    headerTooltip: 'Time spent with the left side of vehicle up',
    valueFormatter: twoDecimalValueFormatter,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
      allowedCharPattern: '\\d\\-', // allow decimals and negatives.
      numberParser: (text: string | null) => {
        return text == null ? null : parseFloat(text);
      },
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: FOUR_DIGIT_WIDTH,
  },
  {
    colId: PitRoadTableColId.JackToVehicleGoes,
    headerName: 'Post Jack',
    headerTooltip: 'Time spent since the vehicle is lowered to acceleration',
    valueFormatter: twoDecimalValueFormatter,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
      allowedCharPattern: '\\d\\-', // allow decimals and negatives.
      numberParser: (text: string | null) => {
        return text == null ? null : parseFloat(text);
      },
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: FOUR_DIGIT_WIDTH,
  },
  {
    colId: PitRoadTableColId.DriverGoes,
    headerName: 'Drv Exit',
    headerTooltip:
      'Drive time exiting pit box and pit road. Moving time from when vehicle first starts moving out of pit box to time it exits pit road (Pit Out)',
    valueFormatter: twoDecimalValueFormatter,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
      allowedCharPattern: '\\d\\-', // allow decimals and negatives.
      numberParser: (text: string | null) => {
        return text == null ? null : parseFloat(text);
      },
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: FOUR_DIGIT_WIDTH,
  },
  {
    colId: PitRoadTableColId.FlagIn,
    headerName: 'Flg In',
    headerTooltip: 'Race condition when entering pit lane',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: [],
      closeOnApply: true,
      cellHeight: 25,
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: 50,
    cellRenderer: flagCellRenderer,
  },
  {
    colId: PitRoadTableColId.FlagOut,
    headerName: 'Flg Out',
    headerTooltip: 'Race condition when exiting pit lane',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: [],
      closeOnApply: true,
      cellHeight: 25,
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: 50,
    cellRenderer: flagCellRenderer,
  },
  {
    colId: PitRoadTableColId.MissedEvents,
    headerName: 'Missed',
    headerTooltip: 'Flag for if marker is missed during pit stop',
    cellRenderer: incompleteCellRenderer,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: [],
      closeOnApply: true,
      cellHeight: 25,
    },
    menuTabs: ['filterMenuTab'],
    minWidth: 16,
    suppressHeaderMenuButton: false,
  },
  {
    colId: PitRoadTableColId.PitInLap,
    headerName: 'Pit Lap',
    headerTooltip: 'Lap when vehicle enters the pit',
    cellRenderer: incompleteCellRenderer,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: THREE_DIGIT_WIDTH,
  },
  {
    colId: PitRoadTableColId.PitInPosition,
    headerName: 'Pit Position In',
    headerTooltip: 'Position when vehicle enters pit road',
    cellRenderer: incompleteCellRenderer,
    minWidth: TWO_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: PitRoadTableColId.PitOutPosition,
    headerName: 'Pit Position Out',
    headerTooltip: 'Position when vehicle exits pit road',
    cellRenderer: incompleteCellRenderer,
    minWidth: TWO_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: PitRoadTableColId.PitPositionDifference,
    headerName: 'Pit +/-',
    headerTooltip: 'Difference in position from vehicle entered vs exited pit road',
    cellRenderer: incompleteCellRenderer,
    minWidth: TWO_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: PitRoadTableColId.AvgPitInPosition,
    headerName: 'Avg Pit In',
    headerTooltip: 'Average position when vehicle enters pit road',
    valueFormatter: threeDecimalValueFormatter,
    cellRenderer: incompleteCellRenderer,
    minWidth: TWO_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: PitRoadTableColId.AvgPitOutPosition,
    headerName: 'Avg Pit Out',
    headerTooltip: 'Average position when vehicle exits pit road',
    valueFormatter: threeDecimalValueFormatter,
    cellRenderer: incompleteCellRenderer,
    minWidth: TWO_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: PitRoadTableColId.AvgPitPositionDifference,
    headerName: 'Avg Pit +/-',
    headerTooltip: 'Average change in position when vehicle enters vs exits pit road',
    valueFormatter: threeDecimalValueFormatter,
    cellRenderer: incompleteCellRenderer,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: TWO_DIGIT_WIDTH,
  },
  {
    colId: PitRoadTableColId.Track,
    headerName: 'Track',
    headerTooltip: 'Event name',
    minWidth: TEXT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: PitRoadTableColId.Date,
    headerName: 'Date',
    headerTooltip: 'Session date',
    valueFormatter: dateValueFormatter,
    minWidth: SESSION_TIME_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: PitRoadTableColId.GreenIn,
    headerName: 'Green In',
    headerTooltip: 'Time from user-selected timing line to pit-in (pit road entry timing line).',
    valueFormatter: threeDecimalValueFormatter,
    cellRenderer: incompleteCellRenderer,
    comparator: stringToNumComparator,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
      allowedCharPattern: '\\d\\-', // allow decimals and negatives.
      numberParser: (text: string | null) => {
        return text == null ? null : parseFloat(text);
      },
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: TEXT_WIDTH,
  },
  {
    colId: PitRoadTableColId.GreenOut,
    headerName: 'Green Out',
    headerTooltip: 'Time from pit-out timing line to user selected on-track timing line.',
    valueFormatter: threeDecimalValueFormatter,
    cellRenderer: incompleteCellRenderer,
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
      allowedCharPattern: '\\d\\-', // allow decimals and negatives.
      numberParser: (text: string | null) => {
        return text == null ? null : parseFloat(text);
      },
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: TEXT_WIDTH,
  },
  {
    colId: PitRoadTableColId.ProPitStopType,
    headerName: 'PRO Pit Stop',
    headerTooltip: 'PRO Pit Stop Type',
    minWidth: TEXT_WIDTH,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    colId: PitRoadTableColId.LeftSideAdjustment,
    headerName: 'L-Adj',
    headerTooltip: 'Left Side Adjustment',
    cellRenderer: booleanCellRenderer,
    minWidth: SIX_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: PitRoadTableColId.RightSideAdjustment,
    headerName: 'R-Adj',
    headerTooltip: 'Right Side Adjustment',
    cellRenderer: booleanCellRenderer,
    minWidth: SIX_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: PitRoadTableColId.FrontRightTireChanged,
    headerName: 'F-RT',
    headerTooltip: 'Front Right Tire Changed',
    cellRenderer: booleanCellRenderer,
    minWidth: SIX_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    colId: PitRoadTableColId.FrontLeftTireChanged,
    headerName: 'F-LT',
    headerTooltip: 'Front Left Tire Changed',
    cellRenderer: booleanCellRenderer,
    minWidth: SIX_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    colId: PitRoadTableColId.RearLeftTireChanged,
    headerName: 'R-LT',
    headerTooltip: 'Rear Left Tire Changed',
    cellRenderer: booleanCellRenderer,
    minWidth: SIX_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    colId: PitRoadTableColId.RearRightTireChanged,
    headerName: 'R-RT',
    headerTooltip: 'Rear Right Tire Changed',
    cellRenderer: booleanCellRenderer,
    minWidth: SIX_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    colId: PitRoadTableColId.BodyRepairs,
    headerName: 'Body Repairs',
    cellRenderer: booleanCellRenderer,
    headerTooltip: 'Body Repairs',
    minWidth: SIX_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
  {
    colId: PitRoadTableColId.PitBoxLeaveTime,
    headerName: 'Pit Box Leave Time',
    headerTooltip: 'Pit Box Leave Time',
    minWidth: SIX_DIGIT_WIDTH,
    cellRenderer: incompleteCellRenderer,
    valueFormatter: threeDecimalValueFormatter,
    suppressHeaderMenuButton: true,
  },
  {
    colId: PitRoadTableColId.PitBoxStopTime,
    headerName: 'Pit Box Stop Time',
    headerTooltip: 'Pit Box Stop Time',
    minWidth: SIX_DIGIT_WIDTH,
    cellRenderer: incompleteCellRenderer,
    valueFormatter: threeDecimalValueFormatter,
    suppressHeaderMenuButton: true,
  },
  {
    colId: PitRoadTableColId.PitStopDuration,
    headerName: 'Pit Stop Duration',
    headerTooltip: 'Pit Stop Duration',
    minWidth: SIX_DIGIT_WIDTH,
    cellRenderer: incompleteCellRenderer,
    valueFormatter: threeDecimalValueFormatter,
    suppressHeaderMenuButton: true,
  },
  {
    colId: PitRoadTableColId.Infractions,
    headerName: 'Infractions',
    headerTooltip: 'Infractions',
    minWidth: TEXT_WIDTH,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    colId: PitRoadTableColId.NumberOfFuelCans,
    headerName: 'Fuel Cans',
    headerTooltip: 'Number of Fuel Cans',
    cellRenderer: incompleteCellRenderer,
    minWidth: THREE_DIGIT_WIDTH,
    suppressHeaderMenuButton: true,
  },
];
